import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { DocumentState } from "@/services/backend/documents/types/state";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { Offer, Patch } from "@/services/backend/omni/offer/types/offer";
import { PositionIdPatch } from "@/services/backend/omni/position/types/position";
import { ScopeOfWorkPatch } from "@/services/backend/omni/scope-of-work/types/scope-of-work";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { AddPositionBaseRequest } from "../../position/types/position-requests";
import { Grouping } from "@/services/backend/omni/position/types/grouping";

const invalidatesOffer = (
  _result: unknown,
  _error: unknown,
  request: { id: string },
) => [
  { type: "OmniOffer" as const, id: request.id },
  { type: "OmniOffer" as const, id: "LIST" },
];

// The backend creates, syncs and deletes the contracts attached to an
// offer of some service kinds with every change of the offer, see
// `milestone.OfferServiceDecorator`, backend/core/omni/domain/milestone/offer_service_decorator_port.go.
// Their IDs are not part of the request, so all contracts are invalidated.
const invalidatesOfferAndContracts = (
  result: unknown,
  error: unknown,
  request: { id: string },
) => [...invalidatesOffer(result, error, request), "OmniContract" as const];

const offerApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    omniOfferCreate: build.mutation<Offer, CreateRequest>({
      query: (request) => ({
        url: `/omni/offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferShow: build.query<Offer, ShowRequest>({
      query: (request) => ({
        url: `/omni/offer`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("OmniOffer"),
    }),
    omniOfferList: build.query<Lst<Offer>, ListRequest>({
      query: (request) => ({
        url: `/omni/offer/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "OmniOffer"),
    }),
    omniOfferPatch: build.mutation<Offer, PatchRequest>({
      query: (request) => ({
        url: `/omni/offer`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    omniOfferCreateScopeOfWork: build.mutation<Offer, CreateScopeOfWorkRequest>(
      {
        query: (request) => ({
          url: `/omni/offer/scope-of-work`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: invalidatesOfferAndContracts,
      },
    ),
    omniOfferPatchScopeOfWork: build.mutation<Offer, PatchScopeOfWorkRequest>({
      query: (request) => ({
        url: `/omni/offer/scope-of-work`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferRemoveScopeOfWork: build.mutation<Offer, RemoveScopeOfWorkRequest>(
      {
        query: (request) => ({
          url: `/omni/offer/scope-of-work`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: invalidatesOfferAndContracts,
      },
    ),
    omniOfferAddScopeOfWorkPosition: build.mutation<
      Offer,
      AddScopeOfWorkPositionRequest
    >({
      query: (request) => ({
        url: `/omni/offer/scope-of-work/position`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferPatchScopeOfWorkPosition: build.mutation<
      Offer,
      PatchScopeOfWorkPositionRequest
    >({
      query: (request) => ({
        url: `/omni/offer/scope-of-work/position`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferPatchScopeOfWorkPositions: build.mutation<
      Offer,
      PatchScopeOfWorkPositionsRequest
    >({
      query: (request) => ({
        url: `/omni/offer/scope-of-work/positions`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferRemoveScopeOfWorkPosition: build.mutation<
      Offer,
      RemoveScopeOfWorkPositionRequest
    >({
      query: (request) => ({
        url: `/omni/offer/scope-of-work/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferRemoveScopeOfWorkPositionsByGrouping: build.mutation<
      Offer,
      RemoveScopeOfWorkPositionsByGroupingRequest
    >({
      query: (request) => ({
        url: `/omni/offer/scope-of-work/positions/grouping`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferRenameScopeOfWorkGrouping: build.mutation<
      Offer,
      RenameScopeOfWorkGroupingRequest
    >({
      query: (request) => ({
        url: `/omni/offer/scope-of-work/positions/grouping`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOfferAndContracts,
    }),
    omniOfferResetReferenceNumber: build.mutation<
      Offer,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/omni/offer/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    omniOfferUpdateState: build.mutation<Offer, UpdateStateRequest>({
      query: (request) => ({
        url: `/omni/offer/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    omniOfferDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/omni/offer`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["OmniOffer", "OmniContract"],
    }),
    // The backend does not render the PDF of an offer yet. The endpoints
    // follow the convention of the other documents: the show route
    // answering to Accept: application/pdf.
    omniOfferPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/omni/offer`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    omniOfferPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/omni/offer`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
      invalidatesTags: invalidatesOffer,
    }),
  }),
});

export const {
  useOmniOfferCreateMutation,
  useOmniOfferShowQuery,
  useOmniOfferListQuery,
  useOmniOfferPatchMutation,
  useOmniOfferCreateScopeOfWorkMutation,
  useOmniOfferPatchScopeOfWorkMutation,
  useOmniOfferRemoveScopeOfWorkMutation,
  useOmniOfferAddScopeOfWorkPositionMutation,
  useOmniOfferPatchScopeOfWorkPositionMutation,
  useOmniOfferPatchScopeOfWorkPositionsMutation,
  useOmniOfferRemoveScopeOfWorkPositionMutation,
  useOmniOfferRemoveScopeOfWorkPositionsByGroupingMutation,
  useOmniOfferRenameScopeOfWorkGroupingMutation,
  useOmniOfferResetReferenceNumberMutation,
  useOmniOfferUpdateStateMutation,
  useOmniOfferDeleteMutation,
  useLazyOmniOfferPdfPreviewQuery,
  useOmniOfferPdfDownloadMutation,
} = offerApi;

export const updateOmniOfferCache = updateCache(offerApi, "omniOfferShow");

/** Mirrors `offer.CreateRequest`, backend/core/omni/domain/offer/service_port.go. */
export type CreateRequest = {
  id: string;
  serviceKind: GSMServiceKind;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  scopeOfWorkServiceVariations?: ServiceVariation[];
};

/** Mirrors `offer.ShowRequest`, backend/core/omni/domain/offer/service_port.go. */
export type ShowRequest = {
  id: string;
};

/** Mirrors `offer.ListRequest`, backend/core/omni/domain/offer/service_port.go. */
export type ListRequest = DocumentBaseFilters &
  DocumentCommonFilters &
  ReferenceNumberFilters & {
    orderBy?: OrdrBy<ListOrderBy>[];
    serviceKind?: Filter<GSMServiceKind>;
    date?: Filter<string>;
    state?: Filter<DocumentState>;
    limit?: number;
    offset?: number;
  };

export type ListOrderBy =
  | "id"
  | "createdAt"
  | "updatedAt"
  | "date"
  | "referenceNumber";

/** Mirrors `offer.PatchRequest`, backend/core/omni/domain/offer/service_port.go. */
export type PatchRequest = Patch & {
  id: string;
};

/** Mirrors `offer.CreateScopeOfWorkRequest`, backend/core/omni/domain/offer/service_port.go. */
export type CreateScopeOfWorkRequest = {
  id: string;
  baseDataId?: string;
  scopeOfWorkId: string;
  serviceObjectId?: string;
  serviceVariation?: ServiceVariation;
  title?: string;
  subtitle?: string;
  text?: string;
};

/** Mirrors `offer.PatchScopeOfWorkRequest`, backend/core/omni/domain/offer/service_port.go. */
export type PatchScopeOfWorkRequest = ScopeOfWorkPatch & {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `offer.RemoveScopeOfWorkRequest`, backend/core/omni/domain/offer/service_port.go. */
export type RemoveScopeOfWorkRequest = {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `offer.AddScopeOfWorkPositionRequest`, backend/core/omni/domain/offer/service_port.go. */
export type AddScopeOfWorkPositionRequest = AddPositionBaseRequest & {
  scopeOfWorkId: string;
};

/** Mirrors `offer.PatchScopeOfWorkPositionRequest`, backend/core/omni/domain/offer/service_port.go. */
export type PatchScopeOfWorkPositionRequest = PositionIdPatch & {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `offer.PatchScopeOfWorkPositionsRequest`, backend/core/omni/domain/offer/service_port.go. */
export type PatchScopeOfWorkPositionsRequest = {
  id: string;
  scopeOfWorkId: string;
  positions: PositionIdPatch[];
};

/** Mirrors `offer.RemoveScopeOfWorkPositionRequest`, backend/core/omni/domain/offer/service_port.go. */
export type RemoveScopeOfWorkPositionRequest = {
  id: string;
  scopeOfWorkId: string;
  positionId: string;
};

/** Mirrors `offer.RemoveScopeOfWorkPositionsByGroupingRequest`, backend/core/omni/domain/offer/service_port.go. */
export type RemoveScopeOfWorkPositionsByGroupingRequest = Grouping & {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `offer.RenameScopeOfWorkGroupingRequest`, backend/core/omni/domain/offer/service_port.go. */
export type RenameScopeOfWorkGroupingRequest = Grouping & {
  id: string;
  scopeOfWorkId: string;
  newGroup?: string;
  newArea?: string;
};

/** Mirrors `offer.UpdateStateRequest`, backend/core/omni/domain/offer/service_port.go. */
export type UpdateStateRequest = {
  id: string;
  state: DocumentState;
  stateSetBy: string;
};

/** Mirrors `offer.DeleteRequest`, backend/core/omni/domain/offer/service_port.go. */
export type DeleteRequest = {
  id: string;
};

/** Mirrors `mjolnirroutes.ShowOfferRequest`, backend/core/omni/adapters/driving/mjolnirroutes/offer_routes.go. */
export type PDFRequest = {
  id: string;
  document?: "" | "offer" | "scope-of-work" | "pricing-by-area" | "data-sheets";
};
